import React, { forwardRef, useMemo, useState } from "react";
import { ActionIcon, Button, Modal, TextInput, Tooltip } from "@mantine/core";
import { LuLink2, LuLink2Off } from "react-icons/lu";
import { useDisclosure } from "@mantine/hooks";
import { getMarkRange } from "@tiptap/core";

const LinkButton = ({ editor, language }) => {
  const [opened, { open, close }] = useDisclosure(false);
  const [url, setUrl] = useState("");
  const [editableText, setEditableText] = useState("");
  const { from, to } = editor.state.selection;

  // When clicking in the middle of an active link, calculate start and end text position
  const linkRange = getMarkRange(
    editor.state.selection.$from,
    editor.state.schema.marks.link
  );

  const selectedText = useMemo(
    () => editor.state.doc.textBetween(from, to, " "),
    [editor, from, to]
  );

  if (!editor) {
    return null;
  }

  const handleLinkButtonClick = () => {
    const previousUrl = editor.getAttributes("link").href;

    if (linkRange) {
      const linkText = editor.state.doc.textBetween(
        linkRange.from,
        linkRange.to,
        " "
      );
      setEditableText(linkText); // Set the text within the link
    } else {
      setEditableText(selectedText || "");
    }

    setUrl(previousUrl || "");
    open();
  };

  // Function to apply the link and update the text
  const applyLink = () => {
    const formattedUrl =
      url.startsWith("http://") || url.startsWith("https://")
        ? url
        : `https://${url}`;

    editor
      .chain()
      .focus()
      .deleteRange(
        linkRange ? { from: linkRange.from, to: linkRange.to } : { from, to }
      ) // Remove current selection
      .insertContent(editableText) // Insert updated text
      .setTextSelection(
        linkRange
          ? { from: linkRange.from, to: linkRange.from + editableText.length }
          : { from, to: from + editableText.length }
      ) // Update selection to new text
      .extendMarkRange("link") // Extend link mark to new text range
      .setLink({ href: formattedUrl }) // Apply the link to the updated text
      .run();

    close(); // Close modal
  };

  const TooltipLinkComponent = forwardRef((props, ref) => (
    <div ref={ref} {...props} style={{ pointerEvents: "none" }}>
      <LuLink2 />
    </div>
  ));
  const TooltipUnlinkComponent = forwardRef((props, ref) => (
    <div ref={ref} {...props} style={{ pointerEvents: "none" }}>
      <LuLink2Off />
    </div>
  ));

  const tooltipDisabled = !editor.isActive("link");

  const submitLinkDisabled = !editableText || !url;

  return (
    <>
      <Modal
        // overlayProps={{
        //   backgroundOpacity: 0.1,
        // }}
        opened={opened}
        onClose={close}
        withCloseButton={false}
        centered
        returnFocus={false}
        // size="xs"
      >
        <div
          onKeyDown={(e) => {
            if (e.code === "Enter" && !submitLinkDisabled) {
              applyLink();
            }
          }}
        >
          <TextInput
            label={
              linkRange
                ? language.TextEditor.EditLinkLabel
                : language.TextEditor.CreateLinkLabel
            }
            placeholder={language.TextEditor.LinkAdressPlaceholder}
            styles={{
              label: { marginBottom: "10px", fontSize: "16px" },
            }}
            // withAsterisk
            size="xs"
            onChange={(e) => setUrl(e.target.value)}
            value={url}
          />
          <TextInput
            placeholder={language.TextEditor.LinkTextPlaceholder}
            // label="Länktext"
            // withAsterisk
            size="xs"
            mt="xs"
            value={editableText}
            onChange={(e) => setEditableText(e.target.value)}
          />
          <div className="text-editor-link-modal-button-wrapper">
            <Button
              // size="xs"
              variant="outline"
              onClick={close}
            >
              Avbryt
            </Button>
            <Button
              // size="xs"
              className={`mantine-Primary-btn`}
              variant="filled"
              onClick={applyLink}
              disabled={submitLinkDisabled}
            >
              OK
            </Button>
          </div>
        </div>
      </Modal>
      <Tooltip
        classNames={{ tooltip: "tooltip-body" }}
        transitionProps={{ duration: 200 }}
        openDelay={500}
        multiline
        events={{ hover: true, focus: true, touch: true }}
        label={language.TextEditor.LinkTooltip}
      >
        <ActionIcon
          className="text-editor-button"
          type="button"
          size="md"
          w="40"
          variant={editor.isActive("link") ? "light" : "subtle"}
          onClick={handleLinkButtonClick}
          color="black"
        >
          <TooltipLinkComponent />
        </ActionIcon>
      </Tooltip>
      <Tooltip
        classNames={{ tooltip: "tooltip-body" }}
        transitionProps={{ duration: 200 }}
        openDelay={500}
        multiline
        events={{ hover: true, focus: true, touch: true }}
        label={language.TextEditor.RemoveLinkTooltip}
        // disabled={tooltipDisabled}
      >
        <ActionIcon
          className="text-editor-button unlink"
          type="button"
          size="md"
          w="40"
          variant={editor.isActive("link") ? "light" : "subtle"}
          disabled={tooltipDisabled}
          onClick={() => editor.chain().focus().unsetLink().run()}
          color="black"
        >
          <TooltipUnlinkComponent />
        </ActionIcon>
      </Tooltip>
    </>
  );
};

export default LinkButton;
